/**
 * fmLayoutShared — helpers/types shared by the FmLayout primitive family.
 * Extracted from FmLayout.tsx so the layout/report/field modules can each stay
 * a focused, sub-500-LOC unit without a circular dependency.
 */

import type { FmSectionBandTone } from './FmReportComposition';

export type FmContentWidthSize = 'prose' | 'form' | 'content';

export const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export const widthTokenFor = (width: FmContentWidthSize): string => {
  switch (width) {
    case 'prose':
      return 'var(--fm-prose-measure)';
    case 'form':
      return 'var(--fm-form-max)';
    case 'content':
    default:
      return 'var(--fm-content-max)';
  }
};

export const sectionBandInnerMaxWidth = (width: FmContentWidthSize): string =>
  `calc(${widthTokenFor(width)} + 2 * var(--fm-container-px))`;

/**
 * Resolve a section band's dark theme island. Only a full-bleed dark/deep band
 * flips the dark `data-theme` (so the primitive owns dark-band token
 * resolution). An embedded section is always a self-consistent light inset card
 * (see the `.fm-section-band--embedded` contrast note in FmLayout.css), so it
 * never flips the theme even for a dark/deep tone — that would repaint its own
 * light-card surface tokens dark and collide with the guarded tone rules
 * Lives here (not in FmReportComposition) so exporting it
 * for testability does not make that component module a mixed component/util
 * export (react-refresh).
 */
export const resolveSectionTheme = (
  embedded: boolean | undefined,
  tone: FmSectionBandTone,
): 'dark' | undefined =>
  !embedded && (tone === 'dark' || tone === 'deep') ? 'dark' : undefined;
